import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const RANK_COLORS = ['#f59e0b', '#94a3b8', '#cd7c2f'];
const RANK_EMOJI = ['🥇', '🥈', '🥉'];

function fmt(n) { return '₹' + (parseInt(n) || 0).toLocaleString('en-IN'); }
function hoursAgo(h) {
  if (h === null) return 'Never';
  if (h < 1) return 'Just now';
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function LeaderboardModal({ onClose }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/dashboard/leaderboard?period=${period}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [period]);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 900, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <div className="modal-title">🏆 Team Performance Leaderboard</div>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>

        {/* Period filter */}
        <div style={{ padding: '0 24px 12px', display: 'flex', gap: 8 }}>
          {['daily', 'weekly', 'monthly'].map(p => (
            <button key={p} className={`btn btn-sm ${period === p ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setPeriod(p)} style={{ textTransform: 'capitalize' }}>
              {p === 'daily' ? 'Today' : p === 'weekly' ? 'This Week' : 'This Month'}
            </button>
          ))}
        </div>

        <div style={{ overflowY: 'auto', padding: '0 24px 24px', display: 'flex', gap: 16 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, width: '100%' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : (
            <>
              {/* Leaderboard list */}
              <div style={{ flex: 1 }}>
                {data?.leaderboard?.map((emp, i) => (
                  <div key={emp.userId}
                    onClick={() => setSelected(selected?.userId === emp.userId ? null : emp)}
                    style={{
                      padding: '14px 16px', marginBottom: 8, borderRadius: 10, cursor: 'pointer',
                      background: selected?.userId === emp.userId ? '#eff6ff' : emp.flags?.noActivityToday ? '#fef2f2' : 'white',
                      border: `1px solid ${selected?.userId === emp.userId ? 'var(--accent)' : emp.flags?.noActivityToday ? '#fecaca' : 'var(--border)'}`,
                      transition: 'all 0.2s'
                    }}>
                    <div className="flex-between">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {/* Rank */}
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: i < 3 ? RANK_COLORS[i] : 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: i < 3 ? 18 : 14, color: i < 3 ? 'white' : 'var(--text-muted)', flexShrink: 0 }}>
                          {i < 3 ? RANK_EMOJI[i] : emp.rank}
                        </div>

                        {/* Avatar */}
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gold-light)', fontWeight: 700, flexShrink: 0 }}>
                          {emp.avatar}
                        </div>

                        <div>
                          <div style={{ fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                            {emp.name}
                            <span className={`badge ${emp.role === 'tl' ? 'badge-navy' : 'badge-blue'}`} style={{ fontSize: 10 }}>
                              {emp.role.toUpperCase()}
                            </span>
                            {emp.flags?.notResponding && <span style={{ fontSize: 10, background: '#fef2f2', color: 'var(--danger)', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>⚠️ {emp.metrics.notContacted} not contacted</span>}
                            {emp.flags?.noActivityToday && <span style={{ fontSize: 10, background: '#fff7ed', color: 'var(--warning)', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>💤 No activity today</span>}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                            Last activity: {hoursAgo(emp.lastActivityAgo)} · Score: {emp.score}
                          </div>
                        </div>
                      </div>

                      {/* Key metrics */}
                      <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--navy)' }}>{emp.metrics.leadsAssigned}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Leads</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontWeight: 800, fontSize: 16, color: '#25d366' }}>{emp.metrics.waSent}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>WA Sent</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--accent)' }}>{emp.metrics.callsMade}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Calls</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--success)' }}>{emp.metrics.enrolled}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Enrolled</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontWeight: 800, fontSize: 14, color: 'var(--success)' }}>{fmt(emp.metrics.paymentsCollected)}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Collected</div>
                        </div>
                        {emp.monthlyTarget > 0 && (
                          <div style={{ textAlign: 'center', minWidth: 60 }}>
                            <div style={{ fontWeight: 800, fontSize: 14, color: emp.metrics.targetAchieved >= 100 ? 'var(--success)' : emp.metrics.targetAchieved >= 50 ? 'var(--warning)' : 'var(--danger)' }}>
                              {emp.metrics.targetAchieved}%
                            </div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Target</div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Expanded detail */}
                    {selected?.userId === emp.userId && (
                      <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                        {[
                          { label: 'Leads Assigned', value: emp.metrics.leadsAssigned, color: 'var(--navy)' },
                          { label: 'Enrolled', value: emp.metrics.enrolled, color: 'var(--success)' },
                          { label: 'WhatsApp Sent', value: emp.metrics.waSent, color: '#25d366' },
                          { label: 'Calls Made', value: emp.metrics.callsMade, color: 'var(--accent)' },
                          { label: 'Follow-ups Done', value: emp.metrics.followUpsDone, color: '#8b5cf6' },
                          { label: 'Proposals Sent', value: emp.metrics.proposalsSent, color: '#f59e0b' },
                          { label: 'Not Contacted (48h)', value: emp.metrics.notContacted, color: emp.metrics.notContacted > 0 ? 'var(--danger)' : 'var(--success)' },
                          { label: 'Amount Collected', value: fmt(emp.metrics.paymentsCollected), color: 'var(--success)' },
                        ].map(m => (
                          <div key={m.label} style={{ background: 'var(--surface)', borderRadius: 8, padding: '10px 14px' }}>
                            <div style={{ fontWeight: 800, fontSize: 18, color: m.color }}>{m.value}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{m.label}</div>
                          </div>
                        ))}

                        {/* Target progress bar */}
                        {emp.monthlyTarget > 0 && (
                          <div style={{ gridColumn: '1 / -1', background: 'var(--surface)', borderRadius: 8, padding: '10px 14px' }}>
                            <div className="flex-between mb-4">
                              <span style={{ fontSize: 12, fontWeight: 600 }}>Monthly Target Progress</span>
                              <span style={{ fontSize: 12, fontWeight: 700, color: emp.metrics.targetAchieved >= 100 ? 'var(--success)' : 'var(--danger)' }}>
                                {fmt(emp.metrics.paymentsCollected)} / {fmt(emp.monthlyTarget)}
                              </span>
                            </div>
                            <div style={{ background: 'var(--border)', borderRadius: 4, height: 8 }}>
                              <div style={{ background: emp.metrics.targetAchieved >= 100 ? 'var(--success)' : emp.metrics.targetAchieved >= 50 ? 'var(--warning)' : 'var(--danger)', borderRadius: 4, height: 8, width: `${Math.min(emp.metrics.targetAchieved, 100)}%`, transition: 'width 0.5s' }} />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {data?.leaderboard?.length === 0 && (
                  <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No data found</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
